import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import { useTourGuiado } from "../../global/tourGuiadoContext";

// Card flutuante no canto inferior direito com checklist dos steps habilitados
// para o user (filtrados por permissoes — companySettings/users/credits).
// Aparece apenas durante o ciclo inicial:
//   welcome_seen && tour_started && tour_active && !allCompleted && !isImpersonate
// Apos completar todos os steps habilitados ele some e nao volta — nem mesmo com switch (D6).
//
// O botao minimizar e estado local (sessao) — nao persiste em localStorage,
// volta a aparecer no proximo refresh.

export default function ProgressCard({ progress, onClose }) {
    const navigate = useNavigate();
    const { enabledSteps } = useTourGuiado();
    const [minimized, setMinimized] = useState(false);

    const handleStepClick = (step) => {
        navigate(step.route);
    };

    const completedCount = enabledSteps.filter(
        (s) => progress?.[s.id]?.completed_at
    ).length;

    if (enabledSteps.length === 0) return null;

    if (minimized) {
        return (
            <MinimizedHolder onClick={() => setMinimized(false)}>
                <MinimizedTitle>Tour guiado</MinimizedTitle>
                <MinimizedCount>
                    {completedCount}/{enabledSteps.length}
                </MinimizedCount>
            </MinimizedHolder>
        );
    }

    return (
        <Holder>
            <Header>
                <HeaderTitle>Tour guiado</HeaderTitle>
                <HeaderActions>
                    <ProgressLabel>
                        {completedCount}/{enabledSteps.length}
                    </ProgressLabel>
                    <MinimizeButton
                        type="button"
                        aria-label="Minimizar"
                        onClick={() => setMinimized(true)}
                    >
                        —
                    </MinimizeButton>
                </HeaderActions>
            </Header>

            <Subtitle>Acesse cada area para concluir o tour.</Subtitle>

            <List>
                {enabledSteps.map((step) => {
                    const isCompleted = !!progress?.[step.id]?.completed_at;
                    return (
                        <ListItem
                            key={step.id}
                            $completed={isCompleted}
                            onClick={() => handleStepClick(step)}
                            data-tour-progress-item={step.id}
                        >
                            <StatusDot
                                $completed={isCompleted}
                                aria-hidden="true"
                            >
                                {isCompleted ? "✓" : ""}
                            </StatusDot>
                            <ItemTitle $completed={isCompleted}>
                                {step.title}
                            </ItemTitle>
                        </ListItem>
                    );
                })}
            </List>
        </Holder>
    );
}

const Holder = styled.div`
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 320px;
    max-width: calc(100vw - 48px);
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-radius: 8px;
    box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
    padding: 16px;
    z-index: 1200;
    font-family: "Montserrat", sans-serif;
`;

const MinimizedHolder = styled.button`
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: #57c4c2;
    color: #ffffff;
    border: none;
    border-radius: 24px;
    padding: 10px 16px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.16);
    cursor: pointer;
    z-index: 1200;
    font-family: "Montserrat", sans-serif;
    display: flex;
    align-items: center;
    gap: 8px;
    &:hover {
        background: #4ba09b;
    }
`;

const MinimizedTitle = styled.span`
    font-weight: 600;
    font-size: 13px;
`;

const MinimizedCount = styled.span`
    font-weight: 500;
    font-size: 12px;
    background: rgba(255, 255, 255, 0.2);
    padding: 2px 8px;
    border-radius: 12px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
`;

const HeaderTitle = styled.h4`
    font-weight: 600;
    font-size: 14px;
    color: #181122;
    margin: 0;
    text-transform: uppercase;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ProgressLabel = styled.span`
    font-weight: 500;
    font-size: 12px;
    color: #57c4c2;
`;

const MinimizeButton = styled.button`
    background: transparent;
    border: none;
    color: #908080;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 4px;
    &:hover {
        color: #181122;
    }
`;

const Subtitle = styled.p`
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #555;
    margin: 0 0 12px 0;
`;

const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ListItem = styled.li`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;
    opacity: ${({ $completed }) => ($completed ? 0.6 : 1)};
    &:hover {
        background: rgba(87, 196, 194, 0.08);
    }
`;

const StatusDot = styled.span`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: ${({ $completed }) =>
        $completed ? "none" : "1px dashed #908080"};
    background: ${({ $completed }) => ($completed ? "#57c4c2" : "transparent")};
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
`;

const ItemTitle = styled.span`
    font-weight: ${({ $completed }) => ($completed ? 400 : 500)};
    font-size: 13px;
    color: #181122;
    text-decoration: ${({ $completed }) =>
        $completed ? "line-through" : "none"};
`;
